// CRM Email Timeline — reusable HubSpot-style interaction history.
// Used by the contact record (Emails tab, /conversations/by-contact/:id) and the
// company record (Activity tab, /conversations/by-company/:id, showContact=true so
// each message shows which contact it was with). Renders sent + received messages
// chronologically, labelled by where they came from (campaign, sent from Sonar,
// synced from a personal inbox). onReply(message) adds a Reply / Follow up action;
// bump refreshKey to reload after a send.
import { useState, useEffect } from 'react';

function when(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
}

function sourceTag(m) {
  if (m.direction === 'inbound') {
    if (m.status === 'bounce') return { cls: 'crmet-tag--rose', label: 'Bounced' };
    if (m.status === 'auto_reply') return { cls: 'crmet-tag--slate', label: 'Auto reply' };
    if (m.source === 'personal_sync') return { cls: 'crmet-tag--slate', label: 'Synced from inbox' };
    return null;
  }
  if (m.status === 'failed') return { cls: 'crmet-tag--rose', label: 'Failed' };
  if (m.source === 'manual') return { cls: 'crmet-tag--orange', label: 'Sent from Sonar' };
  if (m.source === 'logged') return { cls: 'crmet-tag--slate', label: 'Logged from inbox' };
  if (m.source === 'reply') return { cls: 'crmet-tag--slate', label: 'Inbox reply' };
  if (m.campaign_id) return { cls: 'crmet-tag--blue', label: m.campaign_name ? `Campaign · ${m.campaign_name}` : 'Campaign' };
  return null;
}

export default function CrmEmailTimeline({ url, showContact = false, emptyLabel, onReply, refreshKey = 0 }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!messages.length) setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load email history (' + res.status + ')');
        const data = await res.json();
        if (!cancelled) setMessages(Array.isArray(data.messages) ? data.messages : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, refreshKey]);

  if (loading) return <div className="crmr-empty-sm">Loading email history…</div>;
  if (error) return <div className="crmr-empty-sm">{error}</div>;
  if (messages.length === 0) return <div className="crmr-empty-sm">{emptyLabel || 'No emails yet.'}</div>;

  // Newest first reads better on a record; the API returns oldest first.
  const ordered = [...messages].reverse();

  return (
    <div className="crmet-list">
      {ordered.map((m, i) => {
        const inbound = m.direction === 'inbound';
        const id = `${m.direction}-${m.id}-${i}`;
        const open = openId === id;
        const preview = stripHtml(m.body_html) || (m.body_text || '').replace(/\s+/g, ' ').trim();
        const tag = sourceTag(m);
        const canReply = onReply && m.status !== 'failed' && m.status !== 'bounce';
        return (
          <div key={id} className={'crmet-item crmet-item--' + (inbound ? 'in' : 'out')}>
            <span className={'crmet-dot crmet-dot--' + (inbound ? 'in' : 'out')}>{inbound ? '←' : '→'}</span>
            <div className="crmet-body">
              <div className="crmet-top" onClick={() => setOpenId(open ? null : id)}>
                <div className="crmet-topl">
                  <span className={'crmet-dir crmet-dir--' + (inbound ? 'in' : 'out')}>{inbound ? 'Received' : 'Sent'}</span>
                  {m.step_number != null && <span className="crmet-step">Step {m.step_number}</span>}
                  {tag && <span className={'crmet-tag ' + tag.cls}>{tag.label}</span>}
                  {showContact && m.contact_name && <span className="crmet-with">with {m.contact_name}</span>}
                </div>
                <span className="crmet-when">{when(m.occurred_at)}</span>
              </div>
              <div className="crmet-subj" onClick={() => setOpenId(open ? null : id)}>{m.subject || '(no subject)'}</div>
              <div className="crmet-addr">
                {inbound ? `From ${m.from_email}` : `From ${m.from_email || ''} to ${m.to_email}`}
                {m.cc ? ` · Cc ${m.cc}` : ''}
              </div>
              {m.status === 'failed' && m.error_message && <div className="crmet-fail">{m.error_message}</div>}
              {open ? (
                m.body_html
                  ? <div className="crmet-full" dangerouslySetInnerHTML={{ __html: m.body_html }} />
                  : <div className="crmet-full crmet-full--pre">{m.body_text || ''}</div>
              ) : (
                <div className="crmet-preview" onClick={() => setOpenId(id)}>{preview.slice(0, 160)}{preview.length > 160 ? '…' : ''}</div>
              )}
              {canReply && (
                <div className="crmet-actions">
                  <button className="crmet-reply" onClick={() => onReply(m)}>{inbound ? 'Reply' : 'Follow up'}</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
